import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, finalize, takeUntil, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderLine } from 'src/app/core/models/sales/company-sales-order-line.interface';
import { CreateInvoiceFormGroup } from 'src/app/core/models/sales/create-invoice-form-group.interface';
import { CreateInvoiceFormModel } from 'src/app/core/models/sales/create-invoice-form.model';
import { CreateInvoiceLineFormGroup } from 'src/app/core/models/sales/create-invoice-line-form-group.interface';
import { CreateInvoiceLineFormModel } from 'src/app/core/models/sales/create-invoice-line-form.model';
import { CreateInvoiceRequest } from 'src/app/core/models/sales/create-invoice-request.interface';
import { InvoiceErrorBody, InvoiceService } from 'src/app/core/services/invoice.service';

interface CreateInvoiceLineRowView {
  index: number;
  description: string;
  description2: string;
  unitLabel: string;
  remaining: number;
}

@Component({
  selector: 'app-sales-order-create-invoice-modal',
  standalone: true,
  imports: [CommonModule, CoreModule, ReactiveFormsModule, TranslateModule],
  providers: [CreateInvoiceFormModel, CreateInvoiceLineFormModel],
  templateUrl: './sales-order-create-invoice-modal.component.html',
  styleUrl: './sales-order-create-invoice-modal.component.scss',
})
export class SalesOrderCreateInvoiceModalComponent implements OnChanges, OnDestroy {
  readonly formModel = inject(CreateInvoiceFormModel);
  private readonly invoiceService = inject(InvoiceService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrder: CompanySalesOrderDetails | null = null;
  @Input({ required: true }) public unitsOfMeasure: UnitOfMeasureLookup[] = [];
  @Input() public isOpen = false;
  // Resolved human-readable customer name (organization title or contact full name) computed by
  // the parent overview from `clientOptions`. The legacy `customerNumber` (e.g. "C-1234") stays
  // available as a fallback when the lookup misses (e.g. orphan reference, BC-imported order
  // without a matching CRM record).
  @Input() public customerName = '';

  @Output() public readonly closed = new EventEmitter<void>();
  @Output() public readonly invoiceCreated = new EventEmitter<void>();

  public readonly today = SalesOrderCreateInvoiceModalComponent.formatToday();

  public lineRows: CreateInvoiceLineRowView[] = [];
  public customerLabel = '';
  public salesOrderNumberLabel = '';
  public isSubmitting = false;
  public errorMessageKey = '';

  private readonly unsubscribe$ = new Subject<void>();

  // Map structured backend error codes (InvoicesController) to i18n keys for
  // the in-modal banner. Keep in sync with TaskManager.Services.InvoiceErrorCodes.
  private readonly errorCodeKeyMap: Readonly<Record<string, string>> = {
    INVOICE_INVALID_REQUEST: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_INVALID_REQUEST',
    INVOICE_INVALID_STATUS: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_INVALID_STATUS',
    INVOICE_NO_CUSTOMER: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_NO_CUSTOMER',
    INVOICE_UNKNOWN_LINE: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_UNKNOWN_LINE',
    INVOICE_DUPLICATE_LINE: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_DUPLICATE_LINE',
    INVOICE_UOM_MISMATCH: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_UOM_MISMATCH',
    INVOICE_NO_INVENTORY_ITEM: 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_NO_INVENTORY_ITEM',
    INVOICE_OVER_INVOICE: 'COMPONENTS.SALES_ORDERS.ERRORS.OVER_INVOICE',
    // Stage A unified the sales-order error envelope. Closed/Cancelled orders now bubble up
    // ORDER_TERMINAL through the invoice POST gate as well.
    ORDER_TERMINAL: 'COMPONENTS.SALES_ORDERS.ERRORS.ORDER_TERMINAL',
  };

  public get form(): FormGroup<CreateInvoiceFormGroup> {
    return this.formModel.form;
  }

  public get lineForms(): FormArray<FormGroup<CreateInvoiceLineFormGroup>> {
    return this.formModel.lines;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isOpen'] ||
      changes['salesOrder'] ||
      changes['unitsOfMeasure'] ||
      changes['customerName']
    ) {
      if (this.isOpen) {
        this.resetFormFromOrder();
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    if (this.isSubmitting) {
      return;
    }
    this.closed.emit();
  }

  public submitForm(): void {
    if (this.isSubmitting || !this.companyId || !this.salesOrder) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const invoiceDate = (raw.invoiceDate ?? '').trim();
    if (!invoiceDate) {
      return;
    }
    const dueDateRaw = (raw.dueDate ?? '').trim();

    const request: CreateInvoiceRequest = {
      invoiceDate: SalesOrderCreateInvoiceModalComponent.toIsoDateTime(invoiceDate),
      dueDate: dueDateRaw
        ? SalesOrderCreateInvoiceModalComponent.toIsoDateTime(dueDateRaw)
        : null,
      lines: raw.lines.map((line) => ({
        salesOrderLineId: line.salesOrderLineId ?? '',
        quantity: Number(line.quantity ?? 0),
        unitOfMeasureId: Number(line.unitOfMeasureId ?? 0),
      })),
    };

    this.isSubmitting = true;
    this.errorMessageKey = '';

    this.invoiceService
      .createInvoice(this.companyId, this.salesOrder.id, request)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.invoiceCreated.emit();
          this.closed.emit();
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorMessageKey = this.mapErrorKey(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private mapErrorKey(error: HttpErrorResponse): string {
    const code = (error.error as InvoiceErrorBody | null)?.code;
    if (code && this.errorCodeKeyMap[code]) {
      return this.errorCodeKeyMap[code];
    }
    return 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_FAILED';
  }

  private resetFormFromOrder(): void {
    this.errorMessageKey = '';
    this.formModel.clearLines();

    const order = this.salesOrder;
    if (!order) {
      this.lineRows = [];
      this.customerLabel = '';
      this.salesOrderNumberLabel = '';
      this.form.controls.invoiceDate.setValue(this.today);
      this.form.controls.dueDate.setValue(null);
      return;
    }

    // Pre-fill the read-only context strip so the operator immediately sees which order +
    // customer the invoice will be linked to. The link itself is encoded in the URL
    // (`/companies/{companyId}/sales-orders/{salesOrderId}/invoices`) so the backend always
    // attaches the new invoice to the parent sales order.
    // Prefer the resolved human-readable customer name when the parent provided one; fall back
    // to the BC-style `customerNumber` so the field is never blank.
    this.customerLabel = (this.customerName || '').trim() || order.customerNumber || '';
    this.salesOrderNumberLabel = order.number || '';

    const rows: CreateInvoiceLineRowView[] = [];
    order.lines.forEach((line) => {
      const remaining = SalesOrderCreateInvoiceModalComponent.computeRemaining(line);
      if (remaining <= 0) {
        return;
      }
      const unitOfMeasureId = this.resolveUnitOfMeasureId(line);
      if (unitOfMeasureId === null) {
        return;
      }

      const unitLabel = this.resolveUnitLabel(line, unitOfMeasureId);
      this.formModel.addLine({
        salesOrderLineId: line.id,
        quantity: remaining,
        unitOfMeasureId,
        maxQuantity: remaining,
      });

      rows.push({
        index: rows.length,
        description: line.description ?? '',
        description2: line.description2 ?? '',
        unitLabel,
        remaining,
      });
    });

    this.lineRows = rows;
    this.form.controls.invoiceDate.setValue(this.today);
    this.form.controls.dueDate.setValue(null);
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private resolveUnitOfMeasureId(line: CompanySalesOrderLine): number | null {
    if (line.unitOfMeasureId !== null && line.unitOfMeasureId !== undefined) {
      return line.unitOfMeasureId;
    }
    return this.unitsOfMeasure[0]?.id ?? null;
  }

  private resolveUnitLabel(line: CompanySalesOrderLine, unitOfMeasureId: number): string {
    if (line.unitOfMeasureDisplayName) {
      return line.unitOfMeasureDisplayName;
    }
    if (line.unitOfMeasureCode) {
      return line.unitOfMeasureCode;
    }
    const lookup = this.unitsOfMeasure.find((unit) => unit.id === unitOfMeasureId);
    return lookup?.displayName ?? lookup?.code ?? '';
  }

  private static computeRemaining(line: CompanySalesOrderLine): number {
    // An invoice can be created for any non-Cancelled order. The frontend exposes lines
    // whose `invoicedQuantity < quantity`; the backend enforces the over-invoice guard
    // (INVOICE_OVER_INVOICE).
    const totalQty = Number(line.quantity);
    if (!Number.isFinite(totalQty) || totalQty <= 0) {
      return 0;
    }
    const invoiced = Number(line.invoicedQuantity ?? 0);
    const safeInvoiced = Number.isFinite(invoiced) && invoiced > 0 ? invoiced : 0;
    return Math.max(0, totalQty - safeInvoiced);
  }

  private static formatToday(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private static toIsoDateTime(yyyyMmDd: string): string {
    // Backend accepts UTC ISO timestamps; the picker only collects the date portion.
    return `${yyyyMmDd}T00:00:00Z`;
  }
}
